// hooks/useMutedAccountsList.ts — manage-list hook for Settings → Content
// Preferences → Muted accounts.
//
// Loads the full muted-accounts list (name/avatar/handle, not just the bare
// handles muteStore hydrates at boot) and exposes `unmute`, which reuses the
// same toggle usersService.toggleMute the ··· menu calls — so muteStore stays
// the single source of truth and the ··· menu is immediately correct without
// a reload. Components must NOT import usersService or muteStore directly.

import { useCallback, useEffect, useRef, useState } from 'react';
import { usersService } from '@/core/services/usersService';
import type { MutedAccount } from '@/core/models/account';

/**
 * 'unmuted' — the account is genuinely unmuted; the row is removed.
 * 'stale'   — the toggle landed on an already-unmuted account and re-muted it
 *             (another tab, a stale row); the row stays and the whole list
 *             is refetched so it reflects reality.
 * 'error'   — the request itself failed; the row stays untouched.
 * 'busy'    — a request for this handle was already in flight; this call was
 *             a no-op (the double-click/tap guard).
 */
export type UnmuteOutcome = 'unmuted' | 'stale' | 'error' | 'busy';

export interface MutedAccountsListHook {
  accounts: MutedAccount[];
  loading: boolean;
  error: boolean;
  /** Handles currently mid-request — used to disable a row's button so a
   *  double-click can't re-mute the account it just unmuted. */
  pending: Set<string>;
  unmute: (handle: string) => Promise<UnmuteOutcome>;
  /** Re-runs the list fetch — the error state's "Try again" action, and also
   *  called internally when `unmute` discovers a stale row. */
  refresh: () => void;
}

export function useMutedAccountsList(): MutedAccountsListHook {
  const [accounts, setAccounts] = useState<MutedAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [tick, setTick] = useState(0);
  // The actual concurrency guard. React state (`pending` above) can't do this
  // job: two synchronous clicks in the same tick both read it before either
  // update commits, so a state-only check can't tell the second call the
  // first is already running. A ref mutates immediately, so the second call
  // sees the first call's write synchronously — no React render involved.
  const pendingRef = useRef<Set<string>>(new Set());

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    // Unlike a mount-only effect, this one can refire (refresh()/tick), so it
    // DOES need to reset loading/error itself — the initial state alone
    // isn't enough past the first run.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional loader flags, see above
    setLoading(true);
    setError(false);
    usersService
      .mutedAccounts()
      .then((rows) => {
        if (!cancelled) setAccounts(rows);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const unmute = useCallback(
    async (handle: string): Promise<UnmuteOutcome> => {
      if (pendingRef.current.has(handle)) return 'busy';
      pendingRef.current.add(handle);
      setPending((s) => new Set(s).add(handle));
      try {
        // toggleMute flips whatever the server currently has, so a row only
        // stays correct while nothing else has toggled it in the meantime.
        const result = await usersService.toggleMute(handle);
        if (result === false) {
          setAccounts((rows) => rows.filter((a) => a.handle !== handle));
          return 'unmuted';
        }
        if (result === true) {
          // Stale: something else already unmuted this account and our
          // toggle just re-muted it. Refetch instead of patching around it.
          refresh();
          return 'stale';
        }
        return 'error';
      } finally {
        pendingRef.current.delete(handle);
        setPending((s) => {
          const next = new Set(s);
          next.delete(handle);
          return next;
        });
      }
    },
    [refresh],
  );

  return { accounts, loading, error, pending, unmute, refresh };
}
